import React, { ReactNode } from "react";
import { Delete, Edit } from "@mui/icons-material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import {
  canShowRowDelete,
  canShowRowEdit,
  isRowDeleteDisabled,
  type RowVisibilityContext,
} from "@/components/tables/genericCrudTable.rowVisibility";
import type { RowActionDescriptor } from "@/components/tables/rowActions/RowActionItem";

export interface RowActionHandlers {
  edit: (event: React.MouseEvent) => void;
  delete: () => void;
}

export interface RowActionDescriptorContext {
  visibility: RowVisibilityContext;
  isEditDisabled: boolean;
  getDeleteButtonDisabledTooltip?: (row: any) => ReactNode;
  deletesPermanently: boolean;
  handlers: RowActionHandlers;
  t: (key: string, options?: Record<string, unknown>) => string;
}

export const getStandardRowActionDescriptors = (
  row: { original: Record<string, any> },
  ctx: RowActionDescriptorContext
): RowActionDescriptor[] => {
  const { t, handlers } = ctx;

  const isDeleteDisabled = isRowDeleteDisabled(row, ctx.visibility);
  const deleteDisabledTooltip = isDeleteDisabled
    ? ctx.getDeleteButtonDisabledTooltip?.(row)
    : undefined;

  const editAction: RowActionDescriptor = {
    key: 0,
    icon: <Edit />,
    label: t("edit"),
    onClick: handlers.edit,
    disabled: ctx.isEditDisabled,
    wrapButtonInSpan: true,
    showNotAllowedCursorWhenDisabled: true,
    suppressTooltipWhenDisabled: true,
  };

  const deleteAction: RowActionDescriptor = {
    key: 1,
    icon: ctx.deletesPermanently ? <DeleteForeverIcon /> : <Delete />,
    menuIcon: <Delete color="error" />,
    label: t("delete"),
    tooltip:
      deleteDisabledTooltip ??
      (ctx.deletesPermanently ? t("delete_permanently") : t("delete")),
    onClick: handlers.delete,
    disabled: isDeleteDisabled,
    color: "error",
    wrapButtonInSpan: true,
    showNotAllowedCursorWhenDisabled: true,
    suppressTooltipWhenDisabled: !deleteDisabledTooltip,
  };

  const descriptors: RowActionDescriptor[] = [];
  if (canShowRowEdit(row, ctx.visibility)) descriptors.push(editAction);
  if (canShowRowDelete(row, ctx.visibility)) descriptors.push(deleteAction);
  return descriptors;
};
